import React, { useEffect, useMemo } from 'react';

import Loading from '@/components/common/Loading';
import Tab from '@/components/common/Tab';
import { TabItem } from '@/components/common/Tab';
import { UserIdentity } from '@/components/RightProvider/const';
import { LimitConfig } from '@/components/RightProvider/type';
import { useTranslate, UseTranslateType } from '@/hooks/useTranslate';
import { DateType } from '@/store/Payment';
import UserStore from '@/store/User';
import { AIFunction } from '@/utils/const';
import { SubscribeReasonEnum } from '@/utils/eventlog/eventNewConfig';

import ComboContent from './components/comboPanel';
import Footer from './components/footer';
import usePayment from './hook/usePayment';
import BenefitIcon from './icon/benefit';
import CloseIcon from './icon/close';

export interface PaymentModalProps {
  t?: UseTranslateType;
  customTip?: string;
  autoNext?: boolean;
  feature: AIFunction;
  activeTab?: DateType;
  onOk?: () => void;
  onClose?: () => void;
  destroyRef?: any;
  reason?: SubscribeReasonEnum;
}

export interface TabContentProps {
  activeKey: DateType;
  disCount: number;
  disabled: boolean;
  onChange: (value: DateType) => void;
}

const DiscountIcon = () => (
  <svg
    width="63"
    height="19"
    viewBox="0 0 63 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.7863 2.63025C11.8922 2.63025 11.0239 2.92981 10.32 3.48111L5.85105 6.98111C3.8062 8.58261 3.8062 11.6779 5.85106 13.2794L10.32 16.7794C11.0239 17.3307 11.8922 17.6302 12.7863 17.6302H53.4064C55.6155 17.6302 57.4064 15.8394 57.4064 13.6302V6.63025C57.4064 4.42111 55.6155 2.63025 53.4064 2.63025L12.7863 2.63025ZM11.4064 12.1302C12.511 12.1302 13.4064 11.2348 13.4064 10.1302C13.4064 9.02568 12.511 8.13025 11.4064 8.13025C10.3018 8.13025 9.40639 9.02568 9.40639 10.1302C9.40639 11.2348 10.3018 12.1302 11.4064 12.1302Z"
      fill="#FDE043"
    />
    <path
      d="M1.00946 14.7096C6.83908 17.3862 10.2613 14.2044 11.3751 11.7831C11.9242 10.5893 11.025 9.57458 10.5483 9.42375"
      stroke="#FDE043"
    />
  </svg>
);

const Title = ({
  customTip,
  onClose,
  t,
}: Pick<PaymentModalProps, 'customTip' | 'onClose' | 't'>) => (
  <div className="flex items-center justify-center px-[16px] relative">
    {customTip ? (
      <div
        className="absolute top-[0px] bg-[#ffffff] px-[68px] h-[48px] flex items-center justify-center  rounded-[8px] gap-[10px] animate-fadeInOutPaymentTip opacity-0 z-[10]"
        style={{
          animationDelay: '0.1s',
        }}
      >
        <BenefitIcon />
        <span className="text-[18px] font-[500]">{customTip}</span>
      </div>
    ) : null}

    <div
      className={`w-[1080px] text-[32px] pb-[8px] relative text-center font-[600] font-sans text-black ${customTip ? 'animate-fadeInOutPayment opacity-0' : ''}`}
      style={{ animationDelay: customTip ? '1s' : '0s' }}
    >
      <span>{t && t('merror.sub.pricing')}</span>
      <div
        className="fixed top-[30px] right-[30px] flex flex-row items-center justify-center cursor-pointer z-[10]"
        onClick={onClose}
      >
        <CloseIcon />
      </div>
    </div>
  </div>
);
const TabContent = ({ activeKey, disCount, disabled, onChange }: TabContentProps) => {
  const { t } = useTranslate();

  let items: TabItem<DateType>[] = [
    // {
    //   key: 'twoweek',
    //   label: () => {
    //     return <span className="px-[30px]">{t('Time-limited-offer')}</span>;
    //   },
    // },
    {
      key: 'month',
      label: (activeKey) => {
        return (
          <span className={`px-[50px] ${activeKey === 'month' ? 'text-[#fff]' : ''}`}>
            {t('merror.sub.month')}
          </span>
        );
      },
    },
    {
      key: 'year',
      label: () => {
        return (
          <div
            className={`px-[50px] flex items-center gap-[6px]  ${activeKey === 'year' ? 'text-[#fff]' : ''}`}
          >
            <span>{t('merror.sub.year')}</span>
            {disCount > 0 ? (
              <div className="absolute top-[-20px] right-[-20px]  rotate-[340deg]">
                <DiscountIcon />
                <span
                  className={`
                        absolute text-[#fff] top-[-2px] italic inline-block w-[40px] h-full text-[14px]
                        ${disCount >= 10 ? 'right-[6px]' : 'right-[4px]'}`}
                >
                  -<span className="font-itim">{disCount}%</span>
                </span>
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];
  return (
    <div className="px-[28px] pb-[12px] flex justify-center">
      <Tab
        disabled={disabled}
        className="bg-transparent border border-payment-modal-tab-border rounded-[100px] p-[4px] shrink-0"
        itemClassName="py-[6px] text-[18px] font-[500] text-payment-modal-tab-text"
        sliderClassName="bg-payment-modal-tab-item-bg rounded-[100px] text-white"
        type="slider"
        items={items}
        activeKey={activeKey}
        onChange={onChange}
      />
    </div>
  );
};

export const PaymentContentUS: React.FC<PaymentModalProps> = ({
  customTip,
  feature,
  activeTab: defaultActiveTab,
  autoNext = false,
  onClose,
  onOk,
  t,
  destroyRef,
  reason,
}: PaymentModalProps) => {
  const purchaseLimitLoading = UserStore((store) => store.purchaseLimitLoading);

  const {
    handlePay,
    activeTab,
    highlightBenefit,
    formatData,
    weightData,
    loadingStates,
    handleTabChange,
    isPurchaseSuccess,
  } = usePayment({
    t,
    defaultActiveTab,
    feature,
    reason,
  });

  useEffect(() => {
    if (isPurchaseSuccess) {
      onClose && onClose();
      if (autoNext) {
        onOk && onOk();
      }
    }
  }, [isPurchaseSuccess]);

  const onClick = async (
    key: UserIdentity,
    item: LimitConfig[keyof LimitConfig],
    btnIndex: number,
    price: string,
    currency: string,
    selectedCommodityCode: string,
  ) => {
    await handlePay({
      key,
      item,
      btnIndex,
      feature,
      price,
      currency,
      selectedCommodityCode,
    });
  };

  return (
    <div className="bg-payment-modal-bg top-0 left-0 relative">
      <div
        className="absolute"
        style={{
          left: '15%',
          top: '-30%',
          width: '705px',
          height: '705px',
          borderRadius: '705px',
          background: '#BFADFF',
          filter: 'blur(250px)',
        }}
      ></div>

      <div
        className="absolute"
        style={{
          right: '13%',
          top: '-30%',
          width: '705px',
          height: '705px',
          borderRadius: '705px',
          background: '#BFC7FB',
          filter: 'blur(250px)',
        }}
      ></div>

      <div
        className="flex flex-col overflow-auto py-[16px] relative z-10"
        style={{ height: '100vh', width: '100vw' }}
      >
        <Title customTip={customTip} onClose={onClose} t={t} />
        <div className="relative">
          {purchaseLimitLoading ? (
            <div className="min-h-[400px] relative">
              <Loading size={60} />
            </div>
          ) : null}
          <TabContent
            disabled={loadingStates.some((state) => state)}
            activeKey={activeTab}
            onChange={(value) => {
              handleTabChange(value);
            }}
            disCount={highlightBenefit.disCount}
          />

          <ComboContent
            t={t}
            formatData={formatData}
            weightData={weightData}
            activeDateType={activeTab}
            highlightBenefit={highlightBenefit.type}
            loadingStates={loadingStates}
            onClick={onClick}
          />
        </div>

        <Footer t={t} destroyRef={destroyRef} activeTab={activeTab} feature={feature} />
      </div>
    </div>
  );
};
